import React from 'react';

import {getDefaultAvailableColumnsForTable} from 'insights/analytics-queries/block-editor/sql-template-form/SQLTemplateFormUtil';
import {WarehouseTableData} from 'insights/warehouse/warehouseModelsDataLayout';
import Pane from 'ui-library/pane/Pane';

import BioentitySQLTemplateForm from './BioentitySQLTemplateForm';
import interpolateSQLQuery from './interpolation/interpolateSQLQuery';
import makeSQLInterpolationInput from './interpolation/makeSQLInterpolationInput';
import {
  assertIsFormStateInitialized,
  AvailableColumn,
  AvailableColumnProperties,
  ColumnFilter,
  FormType,
  SQLTemplateFormComponentProps,
  SQLTemplateFormState,
} from './SQLTemplateFormTypes';
import SQLTemplateFormWrapper from './SQLTemplateFormWrapper';

type SQLTemplateFormComponent = React.FC<SQLTemplateFormComponentProps>;

const FORM_TYPE_TO_TEMPLATE_FORM_COMPONENT: {
  [k in FormType]: SQLTemplateFormComponent;
} = {
  [FormType.BASIC_BIOENTITY]: BioentitySQLTemplateForm,
};

interface Actions {
  setDraftSQLQuery: (newDraftQuery: string) => void;
  setSQLTemplateFormState: (newSQLTemplateFormState: SQLTemplateFormState) => void;
}

export interface SQLTemplateFormProps {
  actions: Actions;
  additionalActionButtons?: JSX.Element;
  formType: FormType;
  sqlTemplateFormState: SQLTemplateFormState;
}

export default function SQLTemplateForm(props: SQLTemplateFormProps): JSX.Element {
  const SQLTemplateFormComponent = FORM_TYPE_TO_TEMPLATE_FORM_COMPONENT[props.formType];

  function setSQLTemplateFormStateAndQuery(newSQLTemplateFormState: SQLTemplateFormState): void {
    props.actions.setSQLTemplateFormState(newSQLTemplateFormState);

    const sqlInterpolationInput = makeSQLInterpolationInput(newSQLTemplateFormState);
    if (sqlInterpolationInput != null) {
      props.actions.setDraftSQLQuery(interpolateSQLQuery(sqlInterpolationInput));
    }
  }

  async function handleSetSelectedTable(selectedTable: WarehouseTableData): Promise<void> {
    const availableColumns: Array<AvailableColumn> =
      selectedTable == null ? [] : await getDefaultAvailableColumnsForTable(selectedTable);

    setSQLTemplateFormStateAndQuery({
      ...props.sqlTemplateFormState,
      selectedTable,
      availableColumns,
      columnFilters: [],
    });
  }

  function handleSetExcludeDuplicates(excludeDuplicates: boolean): void {
    setSQLTemplateFormStateAndQuery({...props.sqlTemplateFormState, excludeDuplicates});
  }

  function handleUpdateColumnFilters(columnFilters: Array<ColumnFilter>): void {
    assertIsFormStateInitialized(props.sqlTemplateFormState);
    setSQLTemplateFormStateAndQuery({
      ...props.sqlTemplateFormState,
      columnFilters,
    });
  }

  function handleUpdateColumnState(columnIndex: number, columnProperties: AvailableColumnProperties): void {
    assertIsFormStateInitialized(props.sqlTemplateFormState);
    setSQLTemplateFormStateAndQuery({
      ...props.sqlTemplateFormState,
      availableColumns: props.sqlTemplateFormState.availableColumns.map((column, index) =>
        index === columnIndex ? {...column, ...columnProperties} : column
      ),
    });
  }

  /**
   * Update all columns between startIndex and stopIndex (inclusive) with columnProperties.
   */
  function handleUpdateColumnStatesInRange(
    startIndex: number,
    stopIndex: number,
    columnProperties: AvailableColumnProperties
  ): void {
    assertIsFormStateInitialized(props.sqlTemplateFormState);
    setSQLTemplateFormStateAndQuery({
      ...props.sqlTemplateFormState,
      availableColumns: props.sqlTemplateFormState.availableColumns.map((column, index) =>
        index >= startIndex && index <= stopIndex ? {...column, ...columnProperties} : column
      ),
    });
  }

  function handleUpdateAllColumnStates(columnProperties: AvailableColumnProperties): void {
    assertIsFormStateInitialized(props.sqlTemplateFormState);
    handleUpdateColumnStatesInRange(
      0,
      props.sqlTemplateFormState.availableColumns.length - 1,
      columnProperties
    );
  }

  return (
    <Pane
      title="Query"
      toolbar={props.additionalActionButtons}
      dataTestAttrs={{component: 'SQLTemplateForm', element: 'root'}}
    >
      <SQLTemplateFormWrapper>
        <SQLTemplateFormComponent
          sqlTemplateFormState={props.sqlTemplateFormState}
          setSelectedTable={handleSetSelectedTable}
          setExcludeDuplicates={handleSetExcludeDuplicates}
          updateColumnFilters={handleUpdateColumnFilters}
          updateColumnState={handleUpdateColumnState}
          updateColumnStatesInRange={handleUpdateColumnStatesInRange}
          updateAllColumnStates={handleUpdateAllColumnStates}
        />
      </SQLTemplateFormWrapper>
    </Pane>
  );
}
